'use client'

import React, { useEffect, useState, useCallback } from 'react';
import Image from 'next/image';
import { getLogoVariations, resolveLogo } from '@/app/utils/resolve-logo';
import { generateCompanyLogo } from '@/services/companies';

interface CompanyLogoProps {
    logo: string;
    alt: string;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    needsLogoGeneration?: boolean;
    id?: string | number;
    linkedinId?: string;
    domain?: string;
    wrapperClassName?: string;
}

const isProd = process.env.NEXT_PUBLIC_APP_ENVIRONMENT === 'production';

const CompanyLogo: React.FC<CompanyLogoProps> = ({ logo, alt, size = 'md', needsLogoGeneration = false, id, linkedinId, domain, wrapperClassName }) => {
    const [logoIndex, setLogoIndex] = useState(0);
    const [logoVariations, setLogoVariations] = useState<string[]>(getLogoVariations(logo));
    const [logoUrl, setLogoUrl] = useState<string>(logoVariations[0] || '');
    const [isLoading, setIsLoading] = useState<boolean>(needsLogoGeneration);
    const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState<boolean>(false);
    const [noLogoFound, setNoLogoFound] = useState<boolean>(false);
    const [imageError, setImageError] = useState(false);
    const [forceGenTried, setForceGenTried] = useState(false);

    const generateLogo = useCallback(async (force = false) => {
        if (!isProd) {
            return;
        }
        if (!force && !needsLogoGeneration) {
            return;
        }
        if (!id && !linkedinId && !domain) {
            console.log('Cannot generate logo - missing company ID');
            return;
        }
        try {
            setIsLoading(true);
            const response = await generateCompanyLogo(id as number, linkedinId, domain);
            if (response?.logo) {
                setLogoUrl(response.logo);
                setHasAttemptedGeneration(true);
                setNoLogoFound(false);
            } else if (response?.noLogoFound) {
                setNoLogoFound(true);
                setHasAttemptedGeneration(true);
            }
        } catch (error) {
            console.error('Error generating company logo:', error);
            setNoLogoFound(true);
        } finally {
            setIsLoading(false);
        }
    }, [needsLogoGeneration, alt, id, linkedinId, domain]);

    useEffect(() => {        
        // If needsLogoGeneration is true, trigger generation immediately
        if (needsLogoGeneration && !hasAttemptedGeneration) {            
            setIsLoading(true);
            generateLogo();
        }
    }, [logo, needsLogoGeneration, hasAttemptedGeneration, generateLogo]);

    useEffect(() => {
        // If logo is a LinkedIn URL, do not attempt to load it
        if (typeof logo === 'string' && logo.includes('media.licdn.com')) {
            setLogoUrl('');
            setImageError(false);
            setLogoVariations([]);
            setLogoIndex(0);
            return;
        }
        // Generate all variations
        const variations = getLogoVariations(logo);
        setLogoVariations(variations);
        setLogoIndex(0);
        setLogoUrl(variations[0] || '');
        setImageError(false);
    }, [logo]);

    const sizeClasses = {
        xs: 'w-4 h-4',
        sm: 'w-6 h-6',
        md: 'w-8 h-8',
        lg: 'w-12 h-12',
    };
    

    const handleError = () => {
        if (logoIndex < logoVariations.length - 1) {
            setLogoIndex(idx => {
                const nextIdx = idx + 1;
                setLogoUrl(logoVariations[nextIdx] || '');
                return nextIdx;
            });
        } else if (!forceGenTried && (id || linkedinId || domain)) {
            setForceGenTried(true);
            generateLogo(true);
        } else if (!imageError) {
            setImageError(true);
            if (alt && alt !== "Company Logo") {
                setLogoUrl(resolveLogo(alt));
            } else {
                setLogoUrl('');
            }
        } else {
            setLogoUrl('');
        }
    };

    useEffect(() => {
        // When logoIndex changes, update logoUrl
        setLogoUrl(logoVariations[logoIndex] || '');
    }, [logoIndex, logoVariations]);

    // Generate initials from company name
    const getInitials = (name: string) => {
        return name?.trim()
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };
    

    // Ensure we have a valid URL for the Image component
    const imageSrc = logoUrl && typeof logoUrl === 'string' && logoUrl.trim() !== '' ? logoUrl : '';

    // Utility to check if a string is a URL
    function isUrl(str: string) {
        return /^https?:\/\//.test(str);
    }

    useEffect(() => {
        if (typeof logo === 'string' && isUrl(logo)) {
            setLogoVariations([logo]);
            setLogoIndex(0);
            setLogoUrl(logo);
        } else {
            const variations = getLogoVariations(logo);
            setLogoVariations(variations);
            setLogoIndex(0);
            setLogoUrl(variations[0] || '');
        }
        setImageError(false);
    }, [logo]);

    return (
        <div className={`relative ${sizeClasses[size]} ${wrapperClassName || 'rounded-md'} overflow-hidden bg-gray-100`}>
            {isLoading ? (                
                <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600 font-medium">
                    {getInitials(alt)}
                </div>
            ) : noLogoFound ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600 font-medium">
                    {getInitials(alt)}
                </div>
            ) : (imageSrc && isUrl(imageSrc) ? (
                <Image
                    src={imageSrc}
                    alt={alt}
                    fill
                    className="object-contain"
                    onError={handleError}
                    sizes={`${sizeClasses[size]}`}
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600 font-medium">
                    {getInitials(alt)}
                </div>
            ))}
        </div>
    );
};

export default CompanyLogo;
